/**
 * Keeps the phone's status bar / browser toolbar in step with the app theme.
 *
 * Mobile browsers paint that bar from `<meta name="theme-color">`. Without it
 * the bar stays light while the app is dark. The app's theme lives on the
 * `dark` class of <html> (set by ThemeProvider and by the server-backed user
 * settings), so we watch that class rather than either writer.
 */

/** `--background` from styles/_theme.css (light: 210 20% 98%, dark: 224 71.4% 4.1%). */
export const THEME_COLOR = { light: "#f9fafb", dark: "#030712" } as const;

const META_NAME = "theme-color";

export const syncBrowserChrome = (root: HTMLElement = document.documentElement): void => {
  const mode = root.classList.contains("dark") ? "dark" : "light";
  root.style.colorScheme = mode;

  // index.html ships media-scoped defaults for the first paint; once the app
  // knows the real theme, one unscoped tag replaces them.
  const selector = `meta[name="${META_NAME}"]`;
  let stale = document.querySelector(selector);
  while (stale) {
    stale.remove();
    stale = document.querySelector(selector);
  }
  const meta = document.createElement("meta");
  meta.name = META_NAME;
  meta.content = THEME_COLOR[mode];
  document.head.append(meta);
};

/** Syncs now and on every theme change; returns the cleanup. */
export const watchBrowserChrome = (root: HTMLElement = document.documentElement): (() => void) => {
  syncBrowserChrome(root);
  const observer = new MutationObserver(() => {
    syncBrowserChrome(root);
  });
  observer.observe(root, { attributes: true, attributeFilter: ["class"] });
  return () => {
    observer.disconnect();
  };
};
